/**
 * Sales role helpers — the frontend mirror of `app/services/sales_role.py`.
 *
 * The role lives at `user.app_access.sales.role` ('salesperson' |
 * 'sales_director' | 'viewer'), NOT at `user.role` — that column is the
 * PLATFORM role ('admin' | 'viewer' | 'user') and its values overlap ours.
 * Reading the wrong one is not a loud failure: a director whose platform role
 * is 'viewer' just quietly gets the salesperson-scoped UI with no salesperson
 * filter, which reads as "the director role isn't working".
 *
 * Keep the role STRINGS identical to the backend's. Testing for 'director'
 * against a backend that says 'sales_director' matches nothing, forever.
 */

/** Resolve the effective sales role from a profile object. */
export function resolveSalesRole(user) {
  if (!user) return null;
  // Platform admins outrank whatever app_access says.
  if (user.role === 'admin') return 'admin';
  const entry = (user.app_access || {}).sales || {};
  return entry.role || null;
}

/** Sees every account in reach — since 2026-09-15 that is EVERY sales role
 *  (app/platform/scope.py): reach is the companies ISG Admin grants, not the
 *  rep's own book, so the salesperson column + view filter are for everyone. */
export function hasFullVisibility(role) {
  return ['admin', 'sales_manager', 'sales_director', 'viewer', 'salesperson'].includes(role);
}

/** May modify data. `viewer` is read-only; everyone else can write. */
export function canWrite(role) {
  return ['admin', 'sales_manager', 'sales_director', 'salesperson'].includes(role);
}

/** Admin-only actions (e.g. verifying a web request match). */
export function canManage(role) {
  return role === 'admin';
}

/** Acting as someone makes the app read-only, which hides every write button — and
 *  "the New quote button randomly disappears" is what that looked like to a director
 *  (2026-10-08). True when the person is acting as someone but could write as
 *  themselves, so a write button can stay visible and offer to stop acting. */
export function writesHiddenByActingAs(store, actingAs) {
  return !!actingAs && canWrite(resolveSalesRole(store.get('user')));
}
